/**
 * Badge — small label for status indicators, tags, and counts.
 *
 * Supports 7 semantic variants, 3 sizes, optional leading icon,
 * and a pulsing dot for live/active status.
 *
 * Non-interactive — announced as text, not a button.
 *
 * @see docs/milestones/M0.6-UI-PRIMITIVES.md §6
 */

import React from 'react'
import { View, Text } from 'react-native'
import { COLORS, FONTS } from '../../lib/brand'
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withRepeat,
  withSequence,
  withTiming,
  useReducedMotion,
} from 'react-native-reanimated'
import { useEffect } from 'react'
import { useTheme } from '../../lib/hooks/use-theme'
import { useSemanticColors, SPACING, RADIUS, DURATION } from '../../lib/theme/tokens'
import { cn } from '../../lib/theme/variants'

// ─── Types ──────────────────────────────────────────────────────────────────

type BadgeVariant =
  | 'default'
  | 'secondary'
  | 'destructive'
  | 'outline'
  | 'success'
  | 'warning'
  | 'info'
type BadgeSize = 'sm' | 'md' | 'lg'

interface BadgeProps {
  /** Display text */
  label: string
  /** Visual style */
  variant?: BadgeVariant
  /** Size */
  size?: BadgeSize
  /** Optional icon rendered before label */
  icon?: React.ReactNode
  /** Whether to show a pulsing dot (for live/active status) */
  pulse?: boolean
  /** Additional NativeWind className */
  className?: string
}

// ─── Size Tokens ────────────────────────────────────────────────────────────

const SIZE_MAP = {
  sm: { height: 18, paddingH: SPACING.xs + 2, fontSize: 12, dotSize: 4 },
  md: { height: 22, paddingH: SPACING.sm, fontSize: 12, dotSize: 5 },
  lg: { height: 28, paddingH: SPACING.sm + SPACING.xs, fontSize: 13, dotSize: 6 },
} as const

// ─── Variant Colors ─────────────────────────────────────────────────────────

function variantColors(
  variant: BadgeVariant,
  colors: ReturnType<typeof useSemanticColors>,
  isDark: boolean,
) {
  switch (variant) {
    case 'default':
      return {
        bg: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(45,42,94,0.12)',
        text: colors.textPrimary,
        border: undefined,
      }
    case 'secondary':
      return {
        bg: 'rgba(239,78,74,0.12)',
        text: colors.primary,
        border: undefined,
      }
    case 'destructive':
      return {
        bg: COLORS.coral.DEFAULT,
        text: '#FFFFFF',
        border: undefined,
      }
    case 'outline':
      return {
        bg: 'transparent',
        text: colors.textPrimary,
        border: colors.border,
      }
    case 'success':
      return {
        bg: 'rgba(5,150,105,0.12)',
        text: colors.success,
        border: undefined,
      }
    case 'warning':
      return {
        bg: 'rgba(217,119,6,0.12)',
        text: colors.warning,
        border: undefined,
      }
    case 'info':
      return {
        bg: 'rgba(37,99,235,0.12)',
        text: colors.info,
        border: undefined,
      }
  }
}

// ─── Pulse Dot ──────────────────────────────────────────────────────────────

function PulseDot({ color, size }: { color: string; size: number }) {
  const reducedMotion = useReducedMotion()
  const opacity = useSharedValue(1)

  useEffect(() => {
    if (reducedMotion) return
    opacity.value = withRepeat(
      withSequence(
        withTiming(1, { duration: DURATION.verySlow }),
        withTiming(0.3, { duration: DURATION.verySlow }),
      ),
      -1,
      true,
    )
  }, [opacity, reducedMotion])

  const dotStyle = useAnimatedStyle(() => ({ opacity: opacity.value }))

  return (
    <Animated.View
      style={[
        {
          width: size,
          height: size,
          borderRadius: size / 2,
          backgroundColor: color,
          marginRight: SPACING.xs,
        },
        reducedMotion ? undefined : dotStyle,
      ]}
    />
  )
}

// ─── Badge ──────────────────────────────────────────────────────────────────

export function Badge({
  label,
  variant = 'default',
  size = 'md',
  icon,
  pulse = false,
  className,
}: BadgeProps) {
  const { isDark } = useTheme()
  const semanticColors = useSemanticColors(isDark)
  const colors = variantColors(variant, semanticColors, isDark)
  const tokens = SIZE_MAP[size]

  return (
    <View
      accessibilityRole="text"
      className={cn('self-start flex-row items-center', className)}
      style={{
        height: tokens.height,
        paddingHorizontal: tokens.paddingH,
        borderRadius: RADIUS.full,
        backgroundColor: colors.bg,
        borderWidth: colors.border ? 1 : 0,
        borderColor: colors.border,
      }}
    >
      {pulse && <PulseDot color={colors.text} size={tokens.dotSize} />}
      {icon ? <View style={{ marginRight: SPACING.xs }}>{icon}</View> : null}
      <Text
        style={{
          fontSize: tokens.fontSize,
          fontFamily: FONTS.body,
          color: colors.text,
        }}
        selectable={false}
      >
        {label}
      </Text>
    </View>
  )
}
